import { useSelector } from 'react-redux';
import { CartPrescriptionEye } from '../../types';
import { Region } from '@hiwaldo/sdk';
import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { usePageEnvironmentEffect, useStoreCatalogEffect } from '../../hooks';
import { getLensProductVariantSKU } from '../../utils';
import { CatalogProductVariant } from '@hiwaldo/sdk/interfaces';
import { FunnelPrescription } from '../pages';
import {
  useCreateCartProductVariantEffect,
  useCreateCartLensProductVariantsEffect,
  useModifyCartProductVariantEffect,
  useRemoveCartProductVariantEffect,
  useCreateCartTrialEffect,
  useModifyCartTrialEffect,
  useRemoveCartTrialEffect,
  useCreateCartPrescriptionEffect,
  useModifyCartPrescriptionEffect,
  useCreateCartDoctorEffect,
  useModifyCartDoctorEffect,
  CartDoctorDetails,
  StoreState,
  useBasketEffect,
  CartState,
  FunnelState,
  CreateCartLensProductVariantsArgs,
  useGoogleTagManagerEffect,
} from '../../state';
import { CatalogPowerAttributes } from '../../fixtures';

export function useShoppingCartEffect(
  callback?: () => void,
  openBasket = false
) {
  const router = useRouter();
  const { region } = usePageEnvironmentEffect();

  const { revrollAddToCart } = useGoogleTagManagerEffect();

  const { funnelState, cartState, basketDataState, customerDataState } =
    useSelector((state: StoreState) => {
      return {
        funnelState: state.orders.funnel,
        cartState: state.orders.cart,
        basketDataState: state.data.basket,
        customerDataState: state.data.customer,
      };
    });

  const [isPending, setPending] = useState(false);
  const [isSuccess, setSuccess] = useState(false);

  let leftEye: FunnelPrescription = { status: 'EMPTY' };
  let rightEye: FunnelPrescription = { status: 'EMPTY' };

  if (!cartState.includesPrescription) {
    if (
      customerDataState.loggedIn &&
      customerDataState.customer?.activePrescription
    ) {
      const currentPrescription =
        customerDataState.customer?.activePrescription;

      leftEye =
        !currentPrescription.leftEye?.power ||
        currentPrescription.leftEye.power === 'N/A'
          ? { status: 'NOT_REQUIRED' }
          : {
              status: 'CHOSEN',
              productVariantId: +currentPrescription.leftEye.productVariantId,
              productVariantSku: getLensProductVariantSKU(
                region,
                +currentPrescription.leftEye.productVariantId
              ),
              powerAttributeId:
                (CatalogPowerAttributes as any)[
                  currentPrescription.leftEye.power
                ] || 0,
              powerAttributeValue: currentPrescription.leftEye.power,
            };

      rightEye =
        !currentPrescription.rightEye?.power ||
        currentPrescription.rightEye.power === 'N/A'
          ? { status: 'NOT_REQUIRED' }
          : {
              status: 'CHOSEN',
              productVariantId: +currentPrescription.rightEye.productVariantId,
              productVariantSku: getLensProductVariantSKU(
                region,
                +currentPrescription.rightEye.productVariantId
              ),
              powerAttributeId:
                (CatalogPowerAttributes as any)[
                  currentPrescription.rightEye.power
                ] || 0,
              powerAttributeValue: currentPrescription.rightEye.power,
            };
    }
  }

  if (cartState.includesPrescription) {
    leftEye = !cartState.cart!.prescription!.leftEyeProductVariant
      ? { status: 'NOT_REQUIRED' }
      : {
          status: 'CHOSEN',
          productVariantId:
            cartState.cart!.prescription!.leftEyeProductVariant!.id,
          productVariantSku: getLensProductVariantSKU(
            region,
            +cartState.cart!.prescription!.leftEyeProductVariant!.id
          ),
          powerAttributeId:
            cartState.cart!.prescription!.leftEyeProductVariant!.attributes[0]
              .attribute.id,
          powerAttributeValue:
            cartState.cart!.prescription!.leftEyeProductVariant!.attributes[0]
              .attribute.value,
        };

    rightEye = !cartState.cart!.prescription!.rightEyeProductVariant
      ? { status: 'NOT_REQUIRED' }
      : {
          status: 'CHOSEN',
          productVariantId:
            cartState.cart!.prescription!.rightEyeProductVariant!.id,
          productVariantSku: getLensProductVariantSKU(
            region,
            +cartState.cart!.prescription!.rightEyeProductVariant!.id
          ),
          powerAttributeId:
            cartState.cart!.prescription!.rightEyeProductVariant!.attributes[0]
              .attribute.id,
          powerAttributeValue:
            cartState.cart!.prescription!.rightEyeProductVariant!.attributes[0]
              .attribute.value,
        };
  }

  const [currentLeftEye, setCurrentLeftEye] =
    useState<FunnelPrescription>(leftEye);
  const [currentRightEye, setCurrentRightEye] =
    useState<FunnelPrescription>(rightEye);

  const updateSuccess = () => setSuccess(true);

  const { showBasket, hideBasket, basketOpen } = useBasketEffect();

  const storeCatalog = useStoreCatalogEffect();

  const { createCartProductVariant } = useCreateCartProductVariantEffect(
    updateSuccess,
    showBasket
  );
  const { createCartLensProductVariants } =
    useCreateCartLensProductVariantsEffect(updateSuccess);
  const { modifyCartProductVariant } =
    useModifyCartProductVariantEffect(updateSuccess);
  const { removeCartProductVariant } =
    useRemoveCartProductVariantEffect(updateSuccess);
  const { removeCartTrial } = useRemoveCartTrialEffect(updateSuccess);
  const { createCartDoctor } = useCreateCartDoctorEffect(updateSuccess);
  const { modifyCartDoctor } = useModifyCartDoctorEffect(updateSuccess);
  const { createCartTrial, createCartAndTrial } =
    useCreateCartTrialEffect(updateSuccess);
  const { modifyCartTrial, setSyncPrescription } =
    useModifyCartTrialEffect(updateSuccess);

  const handlePrescription = () => {
    let leftEyeProductVariant: CatalogProductVariant | undefined;
    let rightEyeProductVariant: CatalogProductVariant | undefined;

    if (cartState.includesPrescription) {
      leftEyeProductVariant = cartState.cart?.prescription
        ?.leftEyeProductVariant?.id
        ? storeCatalog.getTrialVariant(
            +cartState.cart.prescription.leftEyeProductVariant.id
          )
        : undefined;

      rightEyeProductVariant = cartState.cart?.prescription
        ?.rightEyeProductVariant?.id
        ? storeCatalog.getTrialVariant(
            +cartState.cart.prescription.rightEyeProductVariant.id
          )
        : undefined;
    }

    if (currentLeftEye.status === 'CHOSEN' && currentLeftEye.productVariantId) {
      leftEyeProductVariant = storeCatalog.getTrialVariant(
        +currentLeftEye.productVariantId
      );
    }

    if (
      currentRightEye.status === 'CHOSEN' &&
      currentRightEye.productVariantId
    ) {
      rightEyeProductVariant = storeCatalog.getTrialVariant(
        +currentRightEye.productVariantId
      );
    }

    const leftEyeProductVariantId = leftEyeProductVariant
      ? +leftEyeProductVariant.id
      : undefined;

    const rightEyeProductVariantId = rightEyeProductVariant
      ? +rightEyeProductVariant.id
      : undefined;

    if (funnelState.productType === 'otp') {
      const leftEyeOTPProductVariant = leftEyeProductVariantId
        ? storeCatalog.getOTPVariant(leftEyeProductVariantId)
        : undefined;

      const rightEyeOTPProductVariant = rightEyeProductVariantId
        ? storeCatalog.getOTPVariant(rightEyeProductVariantId)
        : undefined;

      handleOTPPrescription(
        cartState,
        funnelState,
        clearCartTrial,
        createCartLensProductVariants,
        leftEyeOTPProductVariant ? leftEyeOTPProductVariant.id : undefined,
        rightEyeOTPProductVariant ? rightEyeOTPProductVariant.id : undefined
      );
    } else {
      handleTrialPrescription(
        cartState,
        storeCatalog.getTrialVariant,
        modifyCartTrial,
        leftEyeProductVariantId || undefined,
        rightEyeProductVariantId || undefined
      );
    }
  };

  const { createCartPrescription } =
    useCreateCartPrescriptionEffect(handlePrescription);
  const { modifyCartPrescription } =
    useModifyCartPrescriptionEffect(handlePrescription);

  const addCartProductVariant = (
    productVariantId: number,
    quantity: number
  ) => {
    setSuccess(false);
    setPending(true);
    createCartProductVariant(productVariantId, quantity);

    if (openBasket) {
      showBasket();
    }

    if (region === 'US') {
      const productVariant =
        storeCatalog.getRegionalProductVariant(productVariantId);

      revrollAddToCart(
        [
          {
            id: productVariant?.variant.id || 0,
            title: productVariant?.variantLocale.title || '',
            price: productVariant?.variantLocale.pricing[0].actualPrice || 0,
            imageUrl: '',
            variantSku: productVariant?.variantLocale.sku || '',
            quantity: productVariant?.variantLocale.pricing[0].quantity || 0,
          },
        ],
        cartState.cart?.id
      );
    }
  };

  const updateCartProductVariant = (
    variantId: number,
    productVariantId: number,
    quantity: number
  ) => {
    setSuccess(false);
    setPending(true);
    modifyCartProductVariant(variantId, productVariantId, quantity);

    if (openBasket) {
      showBasket();
    }
  };

  const clearCartProductVariant = (productVariantId: number) => {
    const cartProductVariant = cartState.cart!.productVariants.filter(
      (variant) => variant.productVariantId === productVariantId
    );

    if (cartProductVariant.length) {
      setSuccess(false);
      setPending(true);
      removeCartProductVariant(cartProductVariant[0].id);
    }

    if (region === 'US') {
      const productVariant =
        storeCatalog.getRegionalProductVariant(productVariantId);

      revrollAddToCart(
        [
          {
            id: productVariant?.variant.id || 0,
            title: productVariant?.variantLocale.title || '',
            price: productVariant?.variantLocale.pricing[0].actualPrice || 0,
            imageUrl: '',
            variantSku: productVariant?.variantLocale.sku || '',
            quantity: productVariant?.variantLocale.pricing[0].quantity || 0,
          },
        ],
        cartState.cart?.id
      );
    }
  };

  const addTrialToCart = (
    region: Region,
    trialId: number,
    planId: number,
    frequencyId: number
  ) => {
    setSuccess(false);
    setPending(true);
    setSyncPrescription(true);

    let leftEyeProductVariantId: number | undefined;
    let rightEyeProductVariantId: number | undefined;

    if (cartState.includesPrescription) {
      const leftEyeProductVariant = cartState.cart?.prescription
        ?.leftEyeProductVariant?.id
        ? storeCatalog.getTrialVariant(
            +cartState.cart.prescription.leftEyeProductVariant.id,
            [3, 4].includes(+trialId) ? 'vitamin' : 'daily'
          )
        : undefined;

      leftEyeProductVariantId = leftEyeProductVariant
        ? +leftEyeProductVariant.id
        : undefined;

      const rightEyeProductVariant = cartState.cart?.prescription
        ?.rightEyeProductVariant?.id
        ? storeCatalog.getTrialVariant(
            +cartState.cart.prescription.rightEyeProductVariant.id,
            [3, 4].includes(+trialId) ? 'vitamin' : 'daily'
          )
        : undefined;

      rightEyeProductVariantId = rightEyeProductVariant
        ? +rightEyeProductVariant.id
        : undefined;

      // revrollAddToCart([{
      //   id: leftEyeProductVariant?.id || 0,
      //   title: leftEyeProductVariant?.title || '',
      //   price: leftEyeProductVariant?.locales.find(locale => locale.region === region)?.pricing[0].actualPrice || 0,
      //   imageUrl: '',
      //   variantSku: leftEyeProductVariant?.locales.find(locale => locale.region === region)?.sku || '',
      // },
      // {
      //   id: rightEyeProductVariant?.id || 0,
      //   title: rightEyeProductVariant?.title || '',
      //   price: rightEyeProductVariant?.locales.find(locale => locale.region === region)?.pricing[0].actualPrice || 0,
      //   imageUrl: '',
      //   variantSku: rightEyeProductVariant?.locales.find(locale => locale.region === region)?.sku || '',
      // }]);
    }

    if (cartState.includesTrial) {
      modifyCartTrial(
        +cartState.cart!.trials[0].id,
        +trialId,
        +planId,
        +frequencyId,
        1,
        leftEyeProductVariantId,
        rightEyeProductVariantId
      );
    } else if (cartState.cart) {
      createCartTrial(
        region,
        +trialId,
        +planId,
        +frequencyId,
        1,
        leftEyeProductVariantId,
        rightEyeProductVariantId
      );
    } else {
      const cartCustomerId = customerDataState.customer?.id || undefined;
      createCartAndTrial(
        region,
        +trialId,
        +planId,
        +frequencyId,
        1,
        leftEyeProductVariantId,
        rightEyeProductVariantId,
        cartCustomerId
      );
    }
  };

  const clearCartTrial = () => {
    if (cartState.includesTrial) {
      if (isSuccess) setSuccess(false);
      if (!isPending) setPending(true);

      removeCartTrial(+cartState.cart!.trials[0].id);
    }
  };

  const addPrescriptionToCart = (
    leftEye?: CartPrescriptionEye,
    rightEye?: CartPrescriptionEye,
    prescriptionId?: number
  ) => {
    setPending(true);
    setSyncPrescription(false);

    const leftEyeProductVariant =
      leftEye?.productVariantId && leftEye.productVariantId > 0
        ? storeCatalog.getOTPVariant(+leftEye.productVariantId)
        : undefined;

    const leftEyeProductVariantId = leftEyeProductVariant
      ? +leftEyeProductVariant.id
      : null;

    const rightEyeProductVariant =
      rightEye?.productVariantId && rightEye.productVariantId > 0
        ? storeCatalog.getOTPVariant(+rightEye.productVariantId)
        : undefined;

    const rightEyeProductVariantId = rightEyeProductVariant
      ? +rightEyeProductVariant.id
      : null;

    if (cartState.includesPrescription) {
      modifyCartPrescription(
        +cartState.cart!.prescription!.id,
        leftEyeProductVariantId,
        rightEyeProductVariantId,
        prescriptionId
      );
    } else {
      createCartPrescription(
        leftEyeProductVariantId,
        rightEyeProductVariantId,
        prescriptionId
      );
    }
  };

  const addDoctorToCart = (doctor: CartDoctorDetails) => {
    setSuccess(false);
    setPending(true);

    if (cartState.includesDoctor) {
      modifyCartDoctor(+cartState.cart!.doctor!.id, doctor);
    } else {
      createCartDoctor(doctor);
    }
  };

  useEffect(() => {
    if (isPending && isSuccess && !basketDataState.loading) {
      if (callback) {
        callback();
      }
    }

    router.events.on('routeChangeComplete', hideBasket);

    return () => {
      router.events.off('routeChangeComplete', hideBasket);
    };
  }, [basketDataState.loading, isPending, isSuccess, router]);

  return {
    storeCatalog,
    cartState,
    basketOpen,
    currentLeftEye,
    currentRightEye,
    showBasket,
    hideBasket,
    addCartProductVariant,
    modifyCartTrial,
    updateCartProductVariant,
    clearCartProductVariant,
    addTrialToCart,
    clearCartTrial,
    addPrescriptionToCart,
    addDoctorToCart,
    setCurrentLeftEye,
    setCurrentRightEye,
  };
}

const handleTrialPrescription = (
  cartState: CartState,
  getTrialVariant: (id: number) => CatalogProductVariant | undefined,
  modifyCartTrial: (
    id: number,
    trialId: number,
    planId: number,
    frequencyId: number,
    quantity: number,
    leftEyeProductVariantId: number | undefined,
    rightEyeProductVariantId: number | undefined
  ) => void,
  leftEyeVariantId: number | undefined,
  rightEyeVariantId: number | undefined
): void => {
  const leftEyeProductVariant = leftEyeVariantId
    ? getTrialVariant(leftEyeVariantId)
    : undefined;

  const leftEyeTrialVariantId = leftEyeProductVariant
    ? +leftEyeProductVariant.id
    : undefined;

  const rightEyeProductVariant = rightEyeVariantId
    ? getTrialVariant(rightEyeVariantId)
    : undefined;

  const rightEyeTrialVariantId = rightEyeProductVariant
    ? +rightEyeProductVariant.id
    : undefined;

  modifyCartTrial(
    +cartState.cart!.trials[0].id,
    +cartState.cart!.trials[0].trial.id,
    +cartState.cart!.trials[0].planId,
    +cartState.cart!.trials[0].frequencyId,
    1,
    leftEyeTrialVariantId,
    rightEyeTrialVariantId
  );
};

const handleOTPPrescription = (
  cartState: CartState,
  funnelState: FunnelState,
  clearCartTrial: () => void,
  createCartLensProductVariants: (
    args: CreateCartLensProductVariantsArgs
  ) => void,
  leftEyeProductVariantId: number | undefined,
  rightEyeProductVariantId: number | undefined
): void => {
  if (cartState.includesTrial) {
    clearCartTrial();
  }

  const lensQuantity = funnelState.productOTPType || 6;

  const otpProductVariants = {
    leftEye: undefined as
      | { productVariantId: number; quantity: number }
      | undefined,
    rightEye: undefined as
      | { productVariantId: number; quantity: number }
      | undefined,
    bothEyes: undefined as
      | { productVariantId: number; quantity: number }
      | undefined,
  };

  if (
    leftEyeProductVariantId &&
    leftEyeProductVariantId === rightEyeProductVariantId
  ) {
    otpProductVariants.bothEyes = {
      productVariantId: leftEyeProductVariantId,
      quantity: lensQuantity,
    };
  } else {
    if (leftEyeProductVariantId) {
      otpProductVariants.leftEye = {
        productVariantId: leftEyeProductVariantId,
        quantity: lensQuantity / 2,
      };
    }

    if (rightEyeProductVariantId) {
      otpProductVariants.rightEye = {
        productVariantId: rightEyeProductVariantId,
        quantity: lensQuantity / 2,
      };
    }
  }

  createCartLensProductVariants(otpProductVariants);
};
